import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, Phone, MessageCircle, FileText, ShieldCheck,
  Truck, Award, Clock, Plus, ArrowRight, ArrowUpRight,
} from 'lucide-react';
import { whyChooseUs, whyRentVsBuy, products } from '../data/mockData';

/** Subtle scroll-reveal hook (no library). */
const useReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('reveal-on');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    el.querySelectorAll('[data-reveal]').forEach((node) => {
      node.classList.add('reveal-init');
      io.observe(node);
    });
    return () => io.disconnect();
  }, []);
  return ref;
};

const heroSlides = [
  { id: 1, image: 'https://images.unsplash.com/photo-1510761047613-a5c1d008d780?fm=jpg&q=80&w=2000&auto=format&fit=crop', caption: 'Scaffolding silhouette · golden hour' },
  { id: 2, image: 'https://images.unsplash.com/photo-1758798350669-54cd2e0786a3?fm=jpg&q=80&w=2000&auto=format&fit=crop', caption: 'Workers on scaffolding · bright sky' },
  { id: 3, image: 'https://images.unsplash.com/photo-1732043403360-8ad83723b9a6?fm=jpg&q=80&w=2000&auto=format&fit=crop', caption: 'Modern facade · scaffolded build' },
];

const trustPoints = [
  { id: 'ARK-01', icon: ShieldCheck, title: 'Inspected & Certified', desc: 'Every component is checked, serviced and tagged before dispatch.' },
  { id: 'ARK-02', icon: Truck, title: 'Same-Day Dispatch', desc: 'Fast delivery across Pune, PCMC and surrounding industrial belts.' },
  { id: 'ARK-03', icon: Award, title: 'Project-Grade Steel', desc: 'Cuplock, H Frame, props, jacks — all heavy-duty load-tested grades.' },
  { id: 'ARK-04', icon: Clock, title: 'Flexible Tenure', desc: 'Daily, monthly or project-based hire. Scale up or down anytime.' },
];

const blogTeasers = [
  { tag: 'Buyer Guide', code: 'POST-001', title: 'Cuplock vs H-Frame — which suits your Pune project?', desc: 'A practical comparison of speed, load capacity and cost for slab work, plastering and high-rise builds.' },
  { tag: 'Safety', code: 'POST-002', title: 'Pre-erection safety checklist for PCMC sites', desc: 'A 12-point pre-erection checklist contractors and supervisors must run before going live.' },
  { tag: 'Cost', code: 'POST-003', title: 'Why renting beats buying for Pune contractors', desc: 'Capital, depreciation, storage and theft — the real numbers behind the rental advantage.' },
];

const faqs = [
  {
    q: 'Which areas do you cover for scaffolding rental?',
    a: 'We supply scaffolding on hire across Pune, Pimpri Chinchwad (PCMC), Wakad, Hinjewadi, Baner, Aundh, Chinchwad, Nigdi, Ravet, Hadapsar and Kharadi. We also serve outstation projects in Mumbai, Thane, Nashik, Solapur, Kolhapur, Aurangabad, Satara and Ahmednagar.',
  },
  {
    q: 'What is the minimum rental period?',
    a: 'We offer flexible rental tenures — daily, weekly, monthly and project-based hire. The minimum period depends on the quantity and project type, and we always work with your site schedule. Call us for a customised plan.',
  },
  {
    q: 'Do you provide delivery and pickup?',
    a: 'Yes. We arrange transport, loading and pickup of all scaffolding material. Delivery charges depend on quantity, distance and urgency, and are confirmed upfront as part of your quote.',
  },
  {
    q: 'What deposit is required for scaffolding hire?',
    a: 'A refundable security deposit is collected before dispatch, calculated on the quantity and value of material booked. The deposit is returned in full after pickup once the material is verified back at our yard.',
  },
  {
    q: 'Is your scaffolding safety-tested?',
    a: 'Yes. Every component is inspected, serviced and load-checked before it leaves our yard. We only supply project-grade, heavy-duty steel material that meets standard construction safety norms — no shortcuts on safety.',
  },
  {
    q: 'How quickly can material reach my site?',
    a: 'For most projects in Pune and PCMC we dispatch the same day or next day. Outstation locations across Maharashtra are typically delivered within 24–72 hours depending on quantity and route. Call or WhatsApp us for an exact ETA.',
  },
];

const tickerCities = ['Pune', 'Pimpri Chinchwad', 'Wakad', 'Hinjewadi', 'Baner', 'Aundh', 'Chinchwad', 'Nigdi', 'Ravet', 'Tathawade', 'Hadapsar', 'Kharadi', 'Mumbai', 'Thane', 'Nashik', 'Solapur', 'Kolhapur', 'Aurangabad', 'Satara', 'Ahmednagar'];

const stats = [
  { v: 'Full', l: 'Scaffolding range on rent' },
  { v: '4.8★', l: 'Google rating' },
];

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const pageRef = useReveal();

  useEffect(() => {
    const t = setInterval(() => setCurrentSlide((p) => (p + 1) % heroSlides.length), 5500);
    return () => clearInterval(t);
  }, []);

  const featured = products.slice(0, 6);

  return (
    <div ref={pageRef} className="min-h-screen bg-white text-gray-900" data-testid="home-page">
      {/* ============ HERO (teal) ============ */}
      <section className="relative min-h-[88vh] flex items-center overflow-hidden bg-gradient-to-br from-[#008080] via-[#0a7878] to-[#005a5a] text-white" data-testid="home-hero">
        <div className="absolute inset-0 bp-grid-on-dark opacity-50"></div>
        <div className="spotlight-gold -top-40 -right-32"></div>
        <span className="serial-ghost serial-ghost-light absolute right-4 top-24 text-[14rem] sm:text-[20rem] hidden md:block z-0">2026</span>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-20 pb-32">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="anim-drop">
                <span className="ark-overline ark-overline-light">ARK · MH-PUNE-2026 · ON-RENT</span>
              </div>

              <h1 className="font-display font-black uppercase tracking-tighter leading-[0.88] text-white mt-6 text-5xl sm:text-6xl lg:text-[5.5rem] anim-rise delay-100">
                Scaffolding<br />
                on Rent<br />
                <span className="gold-underline text-[#DAA520]">in Pune &amp; PCMC.</span>
              </h1>

              <div className="mt-6 max-w-xl anim-rise delay-200">
                <p className="text-white/90 text-lg md:text-xl leading-relaxed">
                  Cuplock, H Frame, Props, Jacks &amp; complete scaffolding material — inspected, certified and on-site within hours.
                </p>
                <p className="font-mono text-xs text-white/70 tracking-[0.2em] uppercase mt-3">
                  // Safe · Strong · Site-Ready
                </p>
              </div>

              <div className="mt-10 flex flex-wrap gap-3 anim-rise delay-300">
                <Link to="/contact?source=hero_cta" className="ark-btn-primary" data-testid="hero-get-quote-btn">
                  <FileText size={16} /> Get Quote
                </Link>
                <a
                  href="https://wa.me/919766601155?text=Hi%20ARK%20Scaffolding%2C%20I%20need%20a%20quote%20for%20scaffolding%20on%20rent."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ark-btn-secondary ark-btn-secondary-dark"
                  data-testid="hero-whatsapp-btn"
                >
                  <MessageCircle size={16} /> WhatsApp
                </a>
                <a href="tel:+919766601155" className="inline-flex items-center gap-2 px-6 py-4 text-sm font-mono uppercase tracking-widest font-semibold text-white border border-white/30 hover:bg-white/10 transition rounded" data-testid="hero-call-btn">
                  <Phone size={16} /> Call Now
                </a>
              </div>

              <div className="mt-14 grid grid-cols-2 gap-px bg-white/15 anim-fade delay-500 max-w-md">
                {stats.map((s, i) => (
                  <div key={i} className="stat-card bg-[#006666] p-5 overflow-hidden">
                    <div className="font-display font-black text-3xl md:text-4xl text-white">{s.v}</div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[#DAA520] mt-1.5">{s.l}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* RIGHT: image module */}
            <div className="lg:col-span-5 anim-fade delay-300">
              <div className="corner-frame">
                <div className="relative aspect-[4/5] bg-black/30 border border-white/15 overflow-hidden rounded glow-pulse">
                {heroSlides.map((s, i) => (
                  <img
                    key={s.id}
                    src={s.image}
                    alt={s.caption}
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
                      i === currentSlide ? 'opacity-100' : 'opacity-0'
                    }`}
                  />
                ))}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>

                <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
                  <div className="flex items-center gap-2 bg-black/60 backdrop-blur-md border border-white/15 px-3 py-1.5 text-xs font-mono uppercase tracking-widest text-white">
                    <span className="radar-dot"></span> Live · On-Rent
                  </div>
                  <div className="font-mono text-[10px] tracking-[0.25em] text-white/80 uppercase bg-black/60 px-2 py-1.5">
                    {String(currentSlide + 1).padStart(2, '0')} / {String(heroSlides.length).padStart(2, '0')}
                  </div>
                </div>

                <div className="absolute bottom-6 left-6 right-6">
                  <p className="text-xs font-mono uppercase tracking-[0.25em] text-[#DAA520]">// {heroSlides[currentSlide].caption}</p>
                </div>

                <div className="absolute bottom-6 right-6 flex gap-1.5">
                  <button onClick={() => setCurrentSlide((p) => (p - 1 + heroSlides.length) % heroSlides.length)} className="w-9 h-9 flex items-center justify-center bg-white/15 hover:bg-white/30 backdrop-blur border border-white/20 text-white rounded-sm" aria-label="Previous"><ChevronLeft size={16} /></button>
                  <button onClick={() => setCurrentSlide((p) => (p + 1) % heroSlides.length)} className="w-9 h-9 flex items-center justify-center bg-white/15 hover:bg-white/30 backdrop-blur border border-white/20 text-white rounded-sm" aria-label="Next"><ChevronRight size={16} /></button>
                </div>
              </div>
              </div>
              <div className="mt-3 flex items-center justify-between text-[11px] font-mono uppercase tracking-[0.2em] text-white/60">
                <span>// hero · feed</span>
                <span>img-{heroSlides[currentSlide].id}.jpg</span>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 border-t border-white/15 bg-black/30 backdrop-blur py-3 overflow-hidden">
          <div className="ticker-track gap-12 px-4">
            {[...tickerCities, ...tickerCities].map((c, i) => (
              <span key={i} className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/75 flex items-center gap-3 whitespace-nowrap">
                <span className="w-1 h-1 bg-[#DAA520]"></span> Serving · {c}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ============ TRUST STRIP ============ */}
      <section className="relative py-24 md:py-32 bg-white" data-testid="trust-section">
        <div className="absolute inset-0 bp-grid-fine opacity-50 pointer-events-none"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div data-reveal>
              <span className="ark-overline">// 01 · Why Site Engineers Trust Us</span>
              <h2 className="font-display font-bold uppercase tracking-tight text-[#008080] mt-4 text-4xl md:text-5xl lg:text-6xl leading-[0.95]">
                Built for real<br />construction sites.
              </h2>
            </div>
            <p className="text-gray-600 max-w-md text-base leading-relaxed">
              Heavy-duty, certified scaffolding material handled by people who understand site realities — not warehouse paperwork.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-gray-200">
            {trustPoints.map((t, i) => {
              const Icon = t.icon;
              return (
                <div key={t.id} className="ark-card-beam relative bg-white p-7 md:p-8 group min-h-[280px] flex flex-col" data-testid={`trust-card-${i}`}>
                  <span className="serial-ghost absolute -top-2 right-4 text-[7rem] z-0">{t.id.split('-')[1]}</span>
                  <div className="relative z-10 flex flex-col flex-1">
                    <div className="flex items-center justify-between mb-6">
                      <div className="w-11 h-11 border border-[#008080]/30 bg-[#008080]/5 flex items-center justify-center rounded">
                        <Icon className="text-[#008080]" size={20} />
                      </div>
                      <span className="font-mono text-[10px] tracking-[0.2em] text-gray-400">{t.id}</span>
                    </div>
                    <h3 className="font-display font-bold text-[#008080] text-xl md:text-2xl leading-tight">{t.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed mt-3 flex-1">{t.desc}</p>
                    <ArrowUpRight size={18} className="text-[#008080] mt-6 opacity-50 group-hover:opacity-100 group-hover:text-[#DAA520] transition" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ FEATURED PRODUCTS ============ */}
      <section className="relative py-24 md:py-32 bg-gray-50 border-t border-gray-200" data-testid="featured-products">
        <div className="absolute inset-0 bp-grid opacity-40 pointer-events-none"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div data-reveal>
              <span className="ark-overline">// 02 · The Range</span>
              <h2 className="font-display font-bold uppercase tracking-tight text-[#008080] mt-4 text-4xl md:text-5xl lg:text-6xl leading-[0.95]">
                Featured<br />Scaffolding products.
              </h2>
            </div>
            <Link to="/products" className="ark-btn-secondary self-start md:self-end" data-testid="view-all-products-btn">
              View full range <ArrowRight size={16} />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-200">
            {featured.map((p) => (
              <Link
                key={p.id}
                to={`/contact?product=${encodeURIComponent(p.name)}&source=product_card`}
                className="ark-card-beam group relative bg-white flex flex-col"
                data-testid={`featured-product-${p.id}`}
              >
                <div className="relative aspect-[4/3] bg-gray-50 overflow-hidden">
                  <div className="absolute top-4 left-4 z-20 flex items-center gap-2 bg-white/95 backdrop-blur border border-[#008080]/40 px-2.5 py-1 rounded">
                    <span className="radar-dot"></span>
                    <span className="font-mono text-[10px] tracking-widest uppercase text-[#008080]">On Rent</span>
                  </div>
                  <div className="absolute top-4 right-4 z-20 font-mono text-[10px] tracking-[0.2em] text-gray-500 bg-white/95 px-2 py-1 uppercase border border-gray-200 rounded">
                    SKU · ARK-{String(p.id).padStart(3, '0')}
                  </div>
                  <div className="absolute bottom-4 left-4 z-20 bg-white p-1 rounded-sm shadow">
                    <img src="https://customer-assets.emergentagent.com/job_8a6bf79d-629c-4621-9de5-4520ae741e2c/artifacts/15sezi0u_f665ed4a-3e98-483b-93e6-39a122c5327d.jpeg" alt="ARK" className="w-7 h-7 object-contain" />
                  </div>
                  <img src={p.image} alt={`${p.name} scaffolding on rent in Pune`} loading="lazy" className="absolute inset-0 w-full h-full object-contain p-8 group-hover:scale-110 transition-transform duration-700" />
                </div>
                <div className="p-6 border-t border-gray-200">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h3 className="font-display font-bold text-2xl text-[#008080]">{p.name}</h3>
                    <ArrowUpRight className="text-gray-400 group-hover:text-[#DAA520] transition" size={20} />
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed line-clamp-2">{p.description}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============ WHY CHOOSE US (asymmetric) ============ */}
      <section className="relative py-24 md:py-32 bg-white border-t border-gray-200">
        <div className="absolute inset-0 bp-grid-fine opacity-30"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="lg:sticky lg:top-32">
                <span className="ark-overline">// 03 · Why ARK</span>
                <h2 className="font-display font-bold uppercase tracking-tight text-[#008080] mt-4 text-4xl md:text-5xl leading-[0.95]">
                  We don't ship parts.<br />We ship <span className="text-[#DAA520]">certainty.</span>
                </h2>
                <p className="text-gray-600 mt-6 leading-relaxed">
                  Six things every site team can rely on, every single dispatch.
                </p>
              </div>
            </div>
            <div className="lg:col-span-8">
              <div className="grid sm:grid-cols-2 gap-px bg-gray-200">
                {whyChooseUs.map((item, i) => (
                  <div key={i} className="ark-card-beam bg-white p-7 group">
                    <span className="font-mono text-[10px] tracking-[0.25em] text-gray-400 uppercase">// {String(i + 1).padStart(2, '0')}</span>
                    <h3 className="font-display font-bold text-[#008080] text-xl md:text-2xl mt-3 mb-3 leading-tight">{item.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ RENT VS BUY ============ */}
      <section className="relative py-24 md:py-32 bg-gray-50 border-t border-gray-200">
        <div className="absolute inset-0 bp-grid opacity-30"></div>
        <div className="spotlight-gold left-1/2 -translate-x-1/2 top-0"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 max-w-3xl mx-auto" data-reveal>
            <span className="ark-overline justify-center">// 04 · The Math</span>
            <h2 className="font-display font-bold uppercase tracking-tight text-[#008080] mt-4 text-4xl md:text-6xl leading-[0.9]">
              Why renting beats <span className="text-[#DAA520]">buying.</span>
            </h2>
            <p className="text-gray-600 mt-6 leading-relaxed">
              Save capital. Skip storage. Eliminate depreciation. Focus on the project, not the asset register.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-200">
            {whyRentVsBuy.map((item, i) => (
              <div key={i} className="ark-card-beam bg-white p-7 group">
                <div className="flex items-center justify-between mb-5">
                  <div className="text-3xl">{item.icon}</div>
                  <span className="font-mono text-[10px] tracking-[0.2em] text-gray-400">RULE-{String(i + 1).padStart(2, '0')}</span>
                </div>
                <h3 className="font-display font-bold text-[#008080] text-lg md:text-xl mb-2 leading-tight">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FAQ ============ */}
      <section className="relative py-24 md:py-32 bg-white border-t border-gray-200" data-testid="faq-section">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4" data-reveal>
              <span className="ark-overline">// 05 · FAQ</span>
              <h2 className="font-display font-bold uppercase tracking-tight text-[#008080] mt-4 text-4xl md:text-5xl leading-[0.95]">
                Common<br />questions.
              </h2>
              <p className="text-gray-600 mt-6 leading-relaxed">
                Quick, honest answers about scaffolding rental in Pune &amp; PCMC. Still have something specific to ask? Reach us directly on WhatsApp.
              </p>
              <div className="mt-8 flex gap-3">
                <a href="https://wa.me/919766601155" target="_blank" rel="noopener noreferrer" className="ark-btn-primary text-xs">
                  <MessageCircle size={14} /> Ask on WhatsApp
                </a>
              </div>
            </div>
            <div className="lg:col-span-8">
              <div className="border-t border-gray-200">
                {faqs.map((f, i) => (
                  <details key={i} className="ark-faq group border-b border-gray-200 py-5" data-testid={`faq-item-${i}`}>
                    <summary className="flex items-center justify-between gap-6 select-none">
                      <div className="flex items-baseline gap-4">
                        <span className="font-mono text-[11px] tracking-[0.25em] text-gray-400">{String(i + 1).padStart(2, '0')}</span>
                        <span className="font-display font-medium text-gray-900 text-lg md:text-xl">{f.q}</span>
                      </div>
                      <span className="faq-icon flex-shrink-0 w-9 h-9 border border-gray-300 flex items-center justify-center text-[#DAA520]">
                        <Plus size={16} />
                      </span>
                    </summary>
                    <div className="pl-10 pr-12 mt-4 text-gray-600 leading-relaxed text-sm">
                      {f.a}
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ BLOG TEASERS ============ */}
      <section className="relative py-24 md:py-32 bg-gray-50 border-t border-gray-200" data-testid="blog-section">
        <div className="absolute inset-0 bp-grid-fine opacity-40"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div>
              <span className="ark-overline">// 06 · Field Notes</span>
              <h2 className="font-display font-bold uppercase tracking-tight text-[#008080] mt-4 text-4xl md:text-5xl lg:text-6xl leading-[0.95]">
                From the<br />ground up.
              </h2>
            </div>
            <p className="text-gray-600 max-w-md">
              Practical guides on scaffolding selection, safety and rental decisions for Pune &amp; PCMC contractors.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-px bg-gray-200">
            {blogTeasers.map((b, i) => (
              <Link
                to="/contact"
                key={i}
                className="ark-card-beam group bg-white p-8 flex flex-col min-h-[320px]"
                data-testid={`blog-card-${i}`}
              >
                <div className="flex items-center justify-between mb-8">
                  <span className="bg-[#DAA520] text-black text-[10px] font-mono uppercase tracking-[0.2em] px-2.5 py-1">{b.tag}</span>
                  <span className="font-mono text-[10px] text-gray-400 tracking-[0.2em]">{b.code}</span>
                </div>
                <h3 className="font-display font-bold text-[#008080] text-xl md:text-2xl leading-tight">{b.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed mt-4 flex-1">{b.desc}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-[#008080] group-hover:text-[#DAA520] transition font-mono text-xs tracking-widest uppercase">
                  Talk to us <ArrowUpRight size={14} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============ REVIEWS ============ */}
      <section className="relative py-24 md:py-32 bg-white border-t border-gray-200">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14" data-reveal>
            <span className="ark-overline justify-center">// 07 · Field Voices</span>
            <h2 className="font-display font-bold uppercase tracking-tight text-[#008080] mt-4 text-4xl md:text-6xl leading-[0.9]">
              4.8<span className="text-[#DAA520]">★</span> on Google.
            </h2>
            <p className="text-gray-500 mt-4 font-mono text-xs tracking-[0.25em] uppercase">// Based on Google Reviews</p>
          </div>

          <div className="grid md:grid-cols-3 gap-px bg-gray-200">
            {[
              { name: 'Rajesh Kumar', initial: 'R', stars: 5, text: 'Excellent quality scaffolding material and very professional service. ARK provided exactly what we needed for our project. Highly recommended.' },
              { name: 'Sandeep Patil', initial: 'S', stars: 5, text: 'Very reliable and prompt delivery. Equipment was in great condition and perfect for our high-rise. Will use them again.' },
              { name: 'Amit Deshmukh', initial: 'A', stars: 4, text: 'Good service and competitive pricing. The team is knowledgeable and helped us choose the right equipment for our needs.' },
            ].map((r, i) => (
              <div key={i} className="ark-card-beam bg-white p-8 group">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 border border-[#008080]/40 bg-[#008080]/5 flex items-center justify-center text-[#008080] font-display font-bold text-lg rounded">{r.initial}</div>
                    <div>
                      <div className="font-display font-bold text-gray-900">{r.name}</div>
                      <div className="font-mono text-[10px] tracking-widest text-gray-400 uppercase">VERIFIED · GOOGLE</div>
                    </div>
                  </div>
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <svg key={s} className={`w-3.5 h-3.5 fill-current ${s <= r.stars ? 'text-[#DAA520]' : 'text-gray-300'}`} viewBox="0 0 20 20">
                        <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                      </svg>
                    ))}
                  </div>
                </div>
                <p className="text-gray-700 text-sm leading-relaxed">"{r.text}"</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <a href="https://www.google.com/maps/search/?api=1&query=ARK+Scaffolding+Pimple+Saudagar+Pune" target="_blank" rel="noopener noreferrer" data-testid="google-reviews-link" className="ark-btn-secondary">
              View All Reviews on Google <ArrowUpRight size={16} />
            </a>
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA (teal) ============ */}
      <section className="relative py-24 md:py-36 bg-gradient-to-br from-[#008080] to-[#005a5a] text-white overflow-hidden">
        <div className="absolute inset-0 bp-grid-on-dark bp-grid-drift opacity-40"></div>
        <div className="spotlight-gold left-1/2 -translate-x-1/2 -top-20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="ark-overline ark-overline-light justify-center">// Get on site</span>
          <h2 className="font-display font-black uppercase tracking-tighter text-white mt-5 text-5xl md:text-7xl lg:text-8xl leading-[0.85]">
            Ready to start<br />
            <span className="text-[#DAA520]">your project?</span>
          </h2>
          <p className="text-white/85 mt-6 max-w-xl mx-auto text-base md:text-lg">
            Get a fast quote for scaffolding rental in Pune, PCMC or anywhere across Maharashtra.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <a href="tel:+919766601155" className="ark-btn-primary" data-testid="bottom-call-btn">
              <Phone size={16} /> Call: +91 9766601155
            </a>
            <a href="https://wa.me/919766601155" target="_blank" rel="noopener noreferrer" className="ark-btn-secondary ark-btn-secondary-dark" data-testid="bottom-whatsapp-btn">
              <MessageCircle size={16} /> WhatsApp Us
            </a>
            <Link to="/contact?source=hero_cta" className="inline-flex items-center gap-2 px-6 py-4 text-sm font-mono uppercase tracking-widest font-semibold text-white border border-white/30 hover:bg-white/10 transition rounded">
              <FileText size={16} /> Get Quote
            </Link>
          </div>
        </div>
      </section>

      {/* ============ Mobile sticky CTA ============ */}
      <div
        className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-xl border-t border-gray-200 grid grid-cols-3 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]"
        data-testid="mobile-sticky-cta"
      >
        <a href="tel:+919766601155" className="flex flex-col items-center justify-center gap-1 py-3 text-[10px] font-mono uppercase tracking-widest text-[#008080]" data-testid="sticky-call-btn">
          <Phone size={16} /> Call
        </a>
        <a
          href="https://wa.me/919766601155?text=Hi%20ARK%20Scaffolding%2C%20I%20need%20a%20quote%20for%20scaffolding%20on%20rent."
          target="_blank"
          rel="noopener noreferrer"
          className="flex flex-col items-center justify-center gap-1 py-3 text-[10px] font-mono uppercase tracking-widest text-[#10B981] border-x border-gray-200"
          data-testid="sticky-whatsapp-btn"
        >
          <MessageCircle size={16} /> WhatsApp
        </a>
        <Link to="/contact?source=hero_cta" className="flex flex-col items-center justify-center gap-1 py-3 text-[10px] font-mono uppercase tracking-widest text-black bg-[#DAA520]" data-testid="sticky-quote-btn">
          <FileText size={16} /> Quote
        </Link>
      </div>
    </div>
  );
};

export default Home;
